import { useEffect, useRef, useState } from 'react'

import { API } from '@/api'
import { Button, Input, message, Modal, Trans } from '@/components'
import { PortProtocol } from '@/consts/machine'
import { Port } from '@/models/instance'
import { debounce } from '@/utils/debounce'

import { Instance } from '../../../models/instance'

const PORT_OP_TYPE = {
  ADD: 1,
  DELETE: 2,
}

export function PublicIPPortModal(props: {
  open: boolean
  instance: Instance
  port?: Port
  mode: number
  onUpdate?: () => void
  onClose: () => void
}) {
  const { open, instance, port, mode, onClose } = props
  const [loading, setLoading] = useState(false)
  const [servicePort, setServicePort] = useState<number | undefined>(
    mode == 1 ? port?.srcPort : undefined,
  )
  const [protocol] = useState<PortProtocol>(
    mode == 1 ? port?.protocol || PortProtocol.SSH : PortProtocol.SSH,
  )
  const [publicIPPort, setPublicIPPort] = useState<number | undefined>(
    mode == 1 ? port?.destPort : undefined,
  )
  const [servicePortError, setServicePortError] = useState<string>('')
  const [publicIPPortError, setPublicIPPortError] = useState<string>('')
  const [checkingPort, setCheckingPort] = useState(false)

  const debouncedCheckPort = useRef(
    debounce(async (port: number) => {
      if (!port) return

      const minPort = instance.nats?.[0].portRange.min || 1
      const maxPort =
        instance.nats?.[0].portRange.max && instance.nats?.[0].portRange.max > 65535
          ? 65535
          : instance.nats?.[0].portRange.max || 65535

      // Skip API check if port is below minimum
      if (port < minPort) {
        setPublicIPPortError(`Port must be between ${minPort} and ${maxPort}`)
        return
      }

      setCheckingPort(true)
      setPublicIPPortError('Checking port availability...')
      try {
        const res = await API.checkNATPort({
          res_id: instance.nats?.[0].net.netResID || '',
          listen_port: port,
        })
        if (res.code === 194) {
          setPublicIPPortError('Port has already in use')
        } else if (res.code === 0) {
          setPublicIPPortError('')
        } else {
          setPublicIPPortError('Failed to check port availability')
        }
      } catch (error) {
        console.error('Error checking port:', error)
        setPublicIPPortError('Failed to check port availability')
      } finally {
        setCheckingPort(false)
      }
    }, 500),
  ).current

  // Cancel debounced check when component unmounts
  useEffect(() => {
    return () => {
      debouncedCheckPort.cancel()
    }
  }, [debouncedCheckPort])

  const validateServicePort = (value: string) => {
    const numValue = parseInt(value)
    if (!value || isNaN(numValue)) {
      setServicePortError('Service port is required')
      return false
    }
    if (numValue < 1 || numValue > 65535) {
      setServicePortError('Port must be between 1 and 65535')
      return false
    }
    setServicePortError('')
    return true
  }

  const validatePublicIPPort = (value: string) => {
    const numValue = parseInt(value)
    if (!value) {
      setPublicIPPortError('')
      return true
    }
    if (isNaN(numValue)) {
      setPublicIPPortError('Port must be a number')
      return false
    }
    const minPort = instance.nats?.[0].portRange.min || 1
    const maxPort = Math.min(instance.nats?.[0].portRange.max || 65535, 65535)
    if (numValue < minPort || numValue > maxPort) {
      setPublicIPPortError(`Port must be between ${minPort} and ${maxPort}`)
      return false
    }
    // Don't set error message here, let the API check handle it
    return true
  }

  useEffect(() => {
    if (publicIPPort) {
      const minPort = instance.nats?.[0].portRange.min || 1
      const maxPort = Math.min(instance.nats?.[0].portRange.max || 65535, 65535)

      // Only check if the port is within valid range
      if (publicIPPort >= minPort && publicIPPort <= maxPort) {
        debouncedCheckPort(publicIPPort)
      }
    } else {
      setPublicIPPortError('')
    }
  }, [publicIPPort, debouncedCheckPort])

  const hasChanges = () => {
    if (!port) return true
    return servicePort !== port.destPort || protocol !== port.protocol
  }

  const onOK = () => {
    if (!servicePort) {
      setServicePortError('Service port is required')
      return
    }

    setLoading(true)
    const updateOperation =
      mode == 1
        ? [
            API.updateInstancePort({
              request_id: instance.displayID,
              type: PORT_OP_TYPE.DELETE,
              add_port_op: {
                ports: JSON.stringify([]),
              },
              remove_port_op: {
                ports: JSON.stringify([{ ...port }]),
              },
            }),
            API.updateInstancePort({
              request_id: instance.displayID,
              type: PORT_OP_TYPE.ADD,
              add_port_op: {
                ports: JSON.stringify([{ srcPort: servicePort, protocol }]),
              },
              remove_port_op: {
                ports: JSON.stringify([]),
              },
            }),
          ]
        : [
            API.updateInstancePort({
              request_id: instance.displayID,
              type: PORT_OP_TYPE.ADD,
              add_port_op: {
                ports: JSON.stringify([{ srcPort: servicePort, destPort: publicIPPort, protocol }]),
              },
              remove_port_op: {
                ports: JSON.stringify([]),
              },
            }),
          ]

    Promise.all(updateOperation)
      .then((responses) => {
        if (responses.every((res) => res.code === 0)) {
          message.success('Task submitted')
          onClose && onClose()
        } else {
          message.error('Failed to update port')
        }
      })
      .catch((error) => {
        console.error('Error updating port:', error)
      })
      .finally(() => {
        setLoading(false)
      })
  }

  return (
    <Modal
      className="w-[640px]"
      open={open}
      onClose={() => {
        onClose && onClose()
      }}
    >
      <Modal.Header>
        <Trans i18nKey="instance.ipPortModal.title">New Port Binding Rules</Trans>
      </Modal.Header>
      <Modal.Body>
        <div className="space-y-4">
          <div>
            <h3 className="text-primary t-sb mb-2 flex">
              <Trans i18nKey="instance.ipPortModal.instanceServicePort">
                Instance Service Port
              </Trans>
              <div className="text-red-500">*</div>
            </h3>
            <Input
              type="number"
              value={servicePort}
              min={1}
              max={65535}
              disabled={loading}
              onChange={(e) => {
                const value = parseInt(e.target.value)
                setServicePort(value)
              }}
              validator={validateServicePort}
            />
            {servicePortError && <div className="mt-2 t-sm text-red-500">{servicePortError}</div>}
            <div className="mt-2 t-sm text-secondary">
              Your port must be a number between 1 to 65535.
            </div>
          </div>
          <div>
            <h3 className="text-primary t-sb mb-2">
              <Trans i18nKey="instance.ipPortModal.publicIPPort">Public IP Port</Trans>
            </h3>
            <Input
              type="number"
              value={publicIPPort}
              min={instance.nats?.[0].portRange.min || 1}
              max={Math.min(instance.nats?.[0].portRange.max || 65535, 65535)}
              disabled={loading || checkingPort}
              onChange={(e) => {
                const value = parseInt(e.target.value)
                setPublicIPPort(value)
              }}
              validator={validatePublicIPPort}
            />
            {publicIPPortError && (
              <div className={`mt-2 t-sm ${checkingPort ? 'text-secondary' : 'text-red-500'}`}>
                {publicIPPortError}
              </div>
            )}
            <div className="mt-2 t-sm text-secondary">
              Your port must be a number between {instance.nats?.[0].portRange.min || 1} to{' '}
              {Math.min(instance.nats?.[0].portRange.max || 65535, 65535)}.
            </div>
          </div>
        </div>
      </Modal.Body>
      <Modal.Footer>
        <Button
          type="outline"
          onClick={() => {
            onClose && onClose()
          }}
        >
          <Trans i18nKey="instance.list.ipPortModalCancel">Cancel</Trans>
        </Button>
        <Button
          type="secondary"
          disabled={
            !servicePort ||
            loading ||
            !hasChanges() ||
            !!servicePortError ||
            !!publicIPPortError ||
            checkingPort
          }
          onClick={onOK}
        >
          <Trans i18nKey="instance.ipPortModal.create">Create</Trans>
        </Button>
      </Modal.Footer>
    </Modal>
  )
}
